/**
 * Content Helpers - Utilitários para geração de conteúdos
 * 
 * ✅ ATUALIZADO: Suporte para nova arquitetura com VM
 *    - Batch começa com status "pending"
 *    - VM muda para "processing"
 *    - Polling considera ambos os status
 */

/**
 * Determinar span do grid baseado no índice (padrão não-uniforme estilo Instagram)
 */
export const getGridSpan = (index, total) => {
  // Padrões predefinidos para criar grid variado
  const patterns = [
    // Padrão 1: Primeiro grande, resto normal
    { col: 'col-span-2', row: 'row-span-2' },  // 0
    { col: 'col-span-1', row: 'row-span-1' },  // 1
    { col: 'col-span-1', row: 'row-span-1' },  // 2
    { col: 'col-span-1', row: 'row-span-2' },  // 3 (alto)
    { col: 'col-span-2', row: 'row-span-1' },  // 4 (largo)
    { col: 'col-span-1', row: 'row-span-1' },  // 5
    { col: 'col-span-1', row: 'row-span-1' },  // 6
    { col: 'col-span-2', row: 'row-span-1' },  // 7 (largo)
    { col: 'col-span-1', row: 'row-span-2' },  // 8 (alto)
    { col: 'col-span-1', row: 'row-span-1' },  // 9
  ];

  // Repetir padrão se houver mais de 10 itens
  const patternIndex = index % patterns.length;
  return { col: 'col-span-1', row: 'row-span-1' };//patterns[patternIndex];
};

/**
 * Mensagens de loading criativas
 */
export const getRandomLoadingMessage = () => {
  const messages = [
    "✨ Colocando a IA para trabalhar...",
    "🎨 Desenhando pixels mágicos...",
    "🚀 Preparando conteúdos incríveis...",
    "🔥 Aquecendo os neurônios artificiais...",
    "💡 Tendo ideias brilhantes...",
    "🎬 Dirigindo cenas perfeitas...",
    "🖼️ Criando obras-primas...",
    "⚡ Carregando criatividade...",
    "🌟 Transformando ideias em realidade...",
    "🎯 Focando em perfeição..."
  ];

  return messages[Math.floor(Math.random() * messages.length)];
};

/**
 * Obter sequência de mensagens para progresso
 * 
 * ✅ ATUALIZADO: Mensagens refletem nova arquitetura
 */
export const getProgressMessages = () => {
  return [
    { step: 0, message: "✓ Projeto criado com sucesso!", duration: 800 },
    { step: 1, message: "✓ Enviando para processamento...", duration: 1000 },
    { step: 2, message: "✓ Preparando modelos de IA...", duration: 900 },
    { step: 3, message: "⏳ Aguardando início da geração...", duration: 1200 },
    { step: 4, message: "🚀 Redirecionando para acompanhar...", duration: 800 }
  ];
};

/**
 * Cores por tipo de conteúdo
 */
export const getContentTypeColor = (type) => {
  const colors = {
    'sale': 'from-red-500 to-orange-500',
    'announcement': 'from-blue-500 to-cyan-500',
    'marketing': 'from-purple-500 to-pink-500',
    'story': 'from-pink-500 to-rose-500',
    'banner': 'from-indigo-500 to-purple-500',
    'post': 'from-green-500 to-emerald-500'
  };
  return colors[type] || 'from-gray-500 to-gray-600';
};

/**
 * Formatar tempo restante
 */
export const formatTimeRemaining = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

/**
 * Calcular tempo médio por conteúdo
 */
export const getAverageCompletionTime = (completedContents) => {
  if (!completedContents || completedContents.length === 0) return null;

  const times = completedContents
    .filter(content => content.created_at && content.completed_at)
    .map(content => {
      const start = new Date(content.created_at);
      const end = new Date(content.completed_at);
      return (end - start) / 1000; // em segundos
    });

  if (times.length === 0) return null;

  const average = times.reduce((sum, time) => sum + time, 0) / times.length;
  return Math.round(average);
};

/**
 * Estimar tempo restante baseado em progresso
 */
export const estimateTimeRemaining = (batch) => {
  if (!batch || !batch.progress) return null;

  const { total, completed } = batch.progress;
  const remaining = total - completed;

  if (remaining === 0) return 0;

  // Buscar conteúdos completados
  const completedContents = batch.contents?.filter(c => c.status === 'completed') || [];
  const avgTime = getAverageCompletionTime(completedContents);

  if (!avgTime) {
    // Usar estimativa padrão se não houver histórico
    return remaining * 15; // 15s por conteúdo (média)
  }

  return remaining * avgTime;
};

/**
 * Validar URL de imagem/vídeo
 */
export const isValidMediaUrl = (url) => {
  if (!url) return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Obter thumbnail de vídeo (primeiro frame ou placeholder)
 */
export const getVideoThumbnail = (videoUrl) => {
  // Por enquanto retorna placeholder
  // Futuramente pode gerar thumbnail real
  return '/video-placeholder.png';
};

/**
 * Truncar texto com reticências
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Formatar tamanho de arquivo
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(2);
  
  return `${size} ${sizes[i]}`;
};

/**
 * Gerar ID único para elementos
 */
export const generateUniqueId = () => {
  return `content_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * ✅ ATUALIZADO: Verificar se batch está ativo (precisa de polling)
 * 
 * Agora considera "pending" como ativo porque:
 * - Backend cria batch com status "pending"
 * - VM detecta e muda para "processing"
 * - Frontend precisa fazer polling em ambos os casos
 * 
 * @param {Object} batch - Objeto do batch
 * @returns {boolean} - true se o batch ainda está sendo processado
 */
export const isBatchActive = (batch) => {
  if (!batch) return false;
  
  // Status que indicam que o batch ainda está em andamento
  const activeStatuses = ['pending', 'processing'];
  
  // Verificar status principal do batch
  if (activeStatuses.includes(batch.status)) {
    return true;
  }
  
  // Verificar progress (caso status esteja desatualizado)
  if (batch.progress) {
    const { pending, processing } = batch.progress;
    if ((pending && pending > 0) || (processing && processing > 0)) {
      return true;
    }
  }
  
  // Verificar conteúdos individuais
  if (batch.contents && Array.isArray(batch.contents)) {
    const hasActiveContent = batch.contents.some(
      content => activeStatuses.includes(content.status)
    );
    if (hasActiveContent) {
      return true;
    }
  }
  
  return false;
};

/**
 * Obter próximo conteúdo a ser processado
 */
export const getNextProcessingContent = (batch) => {
  if (!batch || !batch.contents) return null;
  
  return batch.contents.find(
    content => content.status === 'processing' || content.status === 'pending'
  );
};

/**
 * Calcular sucesso rate
 */
export const calculateSuccessRate = (batch) => {
  if (!batch || !batch.progress) return 0;
  
  const { total, completed } = batch.progress;
  if (total === 0) return 0;
  
  return Math.round((completed / total) * 100);
};

/**
 * ✅ NOVO: Obter texto de status amigável para o usuário
 * 
 * @param {Object} batch - Objeto do batch
 * @returns {string} - Texto descritivo do status
 */
export const getBatchStatusText = (batch) => {
  if (!batch) return 'Desconhecido';
  
  const { status, progress } = batch;
  
  if (status === 'completed') {
    return 'Concluído';
  }
  
  if (status === 'failed') {
    return 'Falhou';
  }
  
  if (status === 'pending') {
    return 'Na fila de processamento';
  }
  
  if (status === 'processing') {
    if (progress) {
      const { completed, total } = progress;
      return `Processando (${completed}/${total})`;
    }
    return 'Processando...';
  }
  
  return status;
};

/**
 * ✅ NOVO: Verificar se batch está aguardando na fila
 * 
 * @param {Object} batch - Objeto do batch
 * @returns {boolean} - true se está aguardando processamento
 */
export const isBatchPending = (batch) => {
  if (!batch) return false;
  return batch.status === 'pending';
};

/**
 * ✅ NOVO: Verificar se batch está sendo processado ativamente
 * 
 * @param {Object} batch - Objeto do batch
 * @returns {boolean} - true se está em processamento ativo
 */
export const isBatchProcessing = (batch) => {
  if (!batch) return false;
  return batch.status === 'processing' || (batch.progress?.processing > 0);
};